import type { ResolverTag } from '@ps/web-core-enums';
import { useTrackers } from '@ps/web-shared-analytics';
import type { ResolverOptions } from '@ps/web-shared-components';
import { useResolver } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';
import type { Primitive } from 'utility-types';

import { select } from '@swf/core/store';

type Query = Record<string, Primitive>;

interface Props {
  resolverTag: ResolverTag | string;
  query?: Query;
  options?: ResolverOptions;
  className?: string;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
}

const ResolverLink: React.FC<React.PropsWithChildren<Props>> = ({
  resolverTag,
  children,
  query,
  options,
  className,
  target
}) => {
  const tag = useResolver(resolverTag, options);
  const { linkClicked } = useTrackers();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);

  const url = useMemo(() => {
    if (!tag) return tag;
    const parsed = new URL(tag, true);
    Object.assign(parsed.query, query);
    return parsed.toString();
  }, [query, tag]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      isMobileEmbedded && window.starsweb?.app.execute(`resolve/${resolverTag}`);
      linkClicked({ destinationUrl: url, resolverTag });
    },
    [isMobileEmbedded, linkClicked, resolverTag, url]
  );

  return isMobileEmbedded ? (
    <StarsLink onClick={onClick} className={className}>
      {children}
    </StarsLink>
  ) : (
    <StarsLink
      target={target || '_blank'}
      onClick={onClick}
      rel="noopener noreferrer"
      href={`${isDesktopEmbedded ? 'external-' : ''}${url}`}
      className={className}
    >
      {children}
    </StarsLink>
  );
};

export default ResolverLink;
